import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { RootState } from 'src/redux/store';
import { ProductSizesClient, ProductSizeRequest, ProductSizeResponse } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';

export interface ProductSize extends ProductSizeResponse { }

export interface State {
    isLoading: boolean;
    error: boolean;
    productSizeList: ProductSize[];
}

const initialState: State = {
    isLoading: false,
    error: false,
    productSizeList: [],
};

const productSizeSlice = createSlice({
    name: 'productSize',
    initialState,
    reducers: {
        startLoading: (state) => {
            state.isLoading = true;
        },
        hasError: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
        },
        getProductSizeListSuccess: (state, action: PayloadAction<ProductSize[]>) => {
            state.productSizeList = action.payload;
            state.isLoading = false;
        },
        addNewProductSizeSuccess: (state, action: PayloadAction<ProductSize>) => {
            const newItem = action.payload;
            state.productSizeList = [newItem, ...state.productSizeList];
            state.isLoading = false;
        },
        updateProductSizeSuccess: (state, action: PayloadAction<ProductSize>) => {
            const updatedItem = action.payload;
            const index = state.productSizeList.findIndex(item => item.productSizeId === updatedItem.productSizeId);
            state.productSizeList = [...state.productSizeList.slice(0, index), updatedItem, ...state.productSizeList.slice(index + 1)];
            state.isLoading = false;
        },
    },
});

// Reducer
export default productSizeSlice.reducer

// Actions
export const {
    startLoading,
    hasError,
    getProductSizeListSuccess,
    addNewProductSizeSuccess,
    updateProductSizeSuccess,
} = productSizeSlice.actions;

// for useSelector
export const selectProductSize = (state: RootState) => state.productSize as State;

// ----------------------------------------------------------------------
export function getAllProductSize(ignoreCache: boolean = false) {
    return async (dispatch, getState) => {
        try {
            const { productSizeList } = selectProductSize(getState());
            if (ignoreCache || productSizeList.length === 0) {
                dispatch(productSizeSlice.actions.startLoading());
                const client = new ProductSizesClient(undefined, axios);
                const response = await client.getAllProductSize(false);
                dispatch(productSizeSlice.actions.getProductSizeListSuccess(response));
            }
        } catch (error) {
            dispatch(productSizeSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function createProductSize(productSize: ProductSize, showNotification: boolean = true) {
    return async (dispatch, getState) => {
        dispatch(productSizeSlice.actions.startLoading());
        try {
            const { productSizeId, ...payload } = productSize;
            const request = payload as ProductSizeRequest;
            const client = new ProductSizesClient(undefined, axios);
            const response = await client.createProductSize(request);
            dispatch(productSizeSlice.actions.addNewProductSizeSuccess(response));
            if (showNotification) {
                toast.success(`${response.productSizeCode} created successfully!`);
            }
        } catch (error) {
            dispatch(productSizeSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function updateProductSize(productSize: ProductSize, showNotification: boolean = true) {
    return async (dispatch, getState) => {
        dispatch(productSizeSlice.actions.startLoading());
        try {
            const { productSizeId, ...payload } = productSize;
            const request = payload as ProductSizeRequest;
            const client = new ProductSizesClient(undefined, axios);
            const response = await client.editProductSize(productSizeId, request);
            dispatch(productSizeSlice.actions.updateProductSizeSuccess(response));
            if (showNotification) {
                toast.success(`${response.productSizeCode} ${response.isActive ? 'updated' : 'deleted'} successfully!`);
            }
        } catch (error) {
            dispatch(productSizeSlice.actions.hasError(true));
            handleError(error);
        }
    };
}